import type { Concept } from '@yuan-math/curriculum';

/**
 * The rest of Grade 4.
 *
 * `elementary.ts` carries the fraction and decimal spine that Grade 4 shares
 * with the grades either side of it — equivalence, comparison, simplifying,
 * decimal place value. This file is the year's own work, and it is the year in
 * which arithmetic stops being about facts and starts being about *methods*:
 * two-digit multiplication, long division, the first arithmetic performed on
 * fractions rather than about them, and the first measurement a student makes
 * with an instrument rather than by counting.
 *
 * Split out rather than merged into `elementary.ts` for the same reason
 * `grade-three.ts` exists: the band is a cohort, and a reviewer asking "what
 * does Grade 4 cover" should be able to read one file.
 *
 * Every prerequisite here points at something already available, so nothing in
 * this file is reachable-but-unstudiable.
 */
export const GRADE_FOUR_CONCEPTS: Concept[] = [
  /* ------------------------------------------ multiplication and division */
  {
    id: 'multiplicative-comparison',
    title: 'Times as Many',
    summary: 'Some comparisons ask how much *more*, and some ask how many *times* — and they are different questions.',
    strand: 'arithmetic',
    levels: ['grade-4'],
    prerequisites: ['multiplication-facts', 'division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'multiply-multi-digit',
    title: 'Multi-Digit Multiplication',
    summary: 'Two-digit by two-digit is four small products in a box — nothing new, only more of it.',
    strand: 'arithmetic',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['multiply-by-tens', 'multiplication-properties'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'long-division',
    title: 'Long Division',
    summary: 'Divide one place at a time, carrying what is left over into the next.',
    strand: 'arithmetic',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['division-remainders', 'multiply-by-tens'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'prime-composite',
    title: 'Prime and Composite Numbers',
    summary: 'A number with exactly two factors cannot be built from smaller ones — everything else can.',
    strand: 'number-theory',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['factors-multiples'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ------------------------------------------------------------ fractions */
  {
    id: 'add-like-fractions',
    title: 'Adding Fractions with the Same Denominator',
    summary: 'Three eighths and two eighths are five eighths — the pieces are already the same size, so you just count them.',
    strand: 'fractions',
    levels: ['grade-4'],
    prerequisites: ['fraction-basics', 'equivalent-fractions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'fraction-times-whole',
    title: 'A Fraction Times a Whole Number',
    summary: 'Multiplying a fraction by 3 is adding it to itself three times — the pieces never change size.',
    strand: 'fractions',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['add-like-fractions', 'multiplication-facts'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ------------------------------------------------------------- decimals */
  {
    id: 'fractions-to-decimals',
    title: 'Fractions and Decimals',
    summary: 'A decimal is a fraction whose denominator you never have to write, because it is always ten or a hundred.',
    strand: 'decimals',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['decimal-place-value', 'equivalent-fractions'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ---------------------------------------------------------- measurement */
  {
    id: 'unit-conversion',
    title: 'Converting Units',
    summary: 'The same quantity, counted in different-sized units — and the smaller the unit, the more of them there are.',
    strand: 'measurement',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['mass-and-capacity', 'multiply-by-tens'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ------------------------------------------------------------- geometry */
  {
    id: 'angle-measure',
    title: 'Measuring Angles',
    summary: 'An angle is an amount of turn, and a degree is the unit it is counted in.',
    strand: 'geometry',
    levels: ['grade-4'],
    prerequisites: ['shape-attributes'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'symmetry',
    title: 'Lines of Symmetry',
    summary: 'A fold that leaves a shape looking untouched — and most shapes have no such fold at all.',
    strand: 'geometry',
    levels: ['grade-4'],
    prerequisites: ['shape-attributes'],
    status: 'available',
    estimatedMinutes: 25,
  },
];
